import type { GameState } from '../state';
import { chance } from '../math/rng';
import type { ActiveEnemy } from './spawn';
import type { EmitFn } from './events';
import { PET_DEFS, PET_DROP_BASE_CHANCE, PET_DROP_CAP, MAX_EQUIPPED_PETS, anyPetDef, petLevel, petBonusPct, type PetBonus } from '../data/pets';
import { districtBonuses } from './districts';

/** Pet sim: drops, collection/equip ops, and the (multiplicative) bonus fold.
 *  Wave pets and overlord minis share one collection; drops and overlord
 *  kill-levels stop at PET_DROP_CAP, the Yard trains to MAX_PET_LEVEL. */

/** Add one level to a pet or mini, up to `cap`. Auto-equips a first find
 *  while there is an open slot, so new pets feel immediate. */
export function grantPet(state: GameState, modelId: string, cap = PET_DROP_CAP): number {
  if (!anyPetDef(modelId)) return 0;
  const cur = state.pets.collection[modelId] ?? 0;
  if (cur >= cap) return cur;
  const next = Math.min(cap, cur + 1);
  state.pets.collection[modelId] = next;
  if (cur === 0 && state.pets.equipped.length < MAX_EQUIPPED_PETS) {
    state.pets.equipped.push(modelId);
  }
  return next;
}

/** Rolled per normal-mob kill once Pets are unlocked (bosses never drop pets). */
export function rollPetDrop(state: GameState, enemy: ActiveEnemy, emit?: EmitFn): void {
  if (!state.pets.unlocked || enemy.isBoss) return;
  const def = PET_DEFS[enemy.visual.modelId];
  if (!def) return;
  const have = state.pets.collection[def.modelId] ?? 0;
  if (have >= PET_DROP_CAP) return; // drop-capped: no wasted rolls
  // 1/1000 for the first copy, halving for each one already owned (1k -> 2k ->
  // 4k ...); the Arcade district still sweetens the odds (Districts, O10).
  const odds = (PET_DROP_BASE_CHANCE / 2 ** have) * (1 + districtBonuses(state).petDrop);
  if (!chance(state.rng, odds)) return;
  const level = grantPet(state, def.modelId);
  emit?.({ kind: 'petDrop', modelId: def.modelId, name: def.name, level });
}

export interface PetOpResult { ok: boolean; reason?: string }

export function equipPet(state: GameState, modelId: string): PetOpResult {
  if (!state.pets.unlocked) return { ok: false, reason: 'Pets are not online yet.' };
  if (!((state.pets.collection[modelId] ?? 0) > 0)) return { ok: false, reason: 'Not collected yet.' };
  if (state.pets.equipped.includes(modelId)) return { ok: false, reason: 'Already deployed.' };
  if (state.pets.equipped.length >= MAX_EQUIPPED_PETS) return { ok: false, reason: `Only ${MAX_EQUIPPED_PETS} pets can deploy at once.` };
  state.pets.equipped.push(modelId);
  return { ok: true };
}

export function unequipPet(state: GameState, modelId: string): PetOpResult {
  const i = state.pets.equipped.indexOf(modelId);
  if (i < 0) return { ok: false, reason: 'Not deployed.' };
  state.pets.equipped.splice(i, 1);
  return { ok: true };
}

export interface PetBonuses {
  damageMult: number;
  scrapMult: number;
  componentMult: number;
  dropRateMult: number;
  regenMult: number;
}

/** Equipped pets fold into one multiplier per bonus type. Same-type pets stack
 *  additively with each other (2% + 5% -> x1.07); types multiply the run.
 *  Overlord minis ride the same pipeline and the same collection. */
export function petBonuses(state: GameState): PetBonuses {
  const pct: Record<PetBonus, number> = { damage: 0, scrap: 0, components: 0, dropRate: 0, regen: 0 };
  if (state.pets.unlocked) {
    for (const modelId of state.pets.equipped) {
      const def = anyPetDef(modelId);
      if (!def) continue;
      pct[def.bonus] += petBonusPct(petLevel(state.pets.collection[modelId] ?? 0));
    }
  }
  return {
    damageMult: 1 + pct.damage / 100,
    scrapMult: 1 + pct.scrap / 100,
    componentMult: 1 + pct.components / 100,
    dropRateMult: 1 + pct.dropRate / 100,
    regenMult: 1 + pct.regen / 100,
  };
}
